import { useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { useQuery } from '@apollo/client/react/hooks/useQuery'
import { Box, Card, Link, Text } from '@factorialco/deprecated-design-system'
import { F0AvatarList, F0Icon, F0TagDot, F0TagRaw } from '@factorialco/f0-react'
import { OneDataCollection, Tabs } from '@factorialco/f0-react/dist/experimental'
import { Bell, Desktop, Envelope, Person, Suitcase } from '@factorialco/f0-react/icons/app'
import {
  EventSidepanelNavigationDocument,
  TrainingsSessionAttendanceStatusEnum,
  TrainingsSessionFrequencyEnum,
} from 'generated/resources/graphql'
import {
  AttendanceSessionEventSidepanelFragment,
  AttendanceSessionSidepanelDocument,
  AttendanceSidepanelFilterTeamNamesDocument,
  UpdateAttendanceEventSidepanelDocument,
  UpdateSessionAttendancesDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import { i18nDateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useAccessCalendarSync } from 'modules/personal_settings/components/Settings/ExternalCalendar/useAccessCalendarSync'
import { useLoadReminders } from 'modules/trainings/hooks/useReminders'
import { useTrainingsDatatable } from 'modules/trainings/hooks/useTrainingsDatatable'
import { DEFAULT_DATA_COLLECTION_STORAGE } from 'modules/trainings/lib/collections'
import { ModifyHoursModal } from 'modules/trainings/components/Revamp/ModifyHoursModal'

interface BulkUpdateParams {
  ids: string[]
  status: TrainingsSessionAttendanceStatusEnum
  all?: boolean
  sessionId?: string
  search?: string
  teamIds?: string[]
  statusFilter?: string[]
}

type TabId = 'details' | 'attendance'

const AttendanceSidepanel = () => {
  const { eventId, classId } = useParams()
  const client = useApolloClient()
  const [activeTab, setActiveTab] = useState<TabId>('details')
  const [modifyHoursModal, setModifyHoursModal] = useState<{
    open: boolean
    firstCompletedDuration: number | null
    selectedCount: number
  }>({ open: false, firstCompletedDuration: null, selectedCount: 0 })
  const [modifyHoursParams, setModifyHoursParams] = useState<{
    ids: string[]
    all?: boolean
    sessionId?: string
    search?: string
    teamIds?: string[]
    statusFilter?: string[]
  } | null>(null)

  const [bulkUpdateTrainingsSessionAttendance] = useMutation(
    UpdateAttendanceEventSidepanelDocument,
    {
      extractorFn: (data) => data.trainingsMutations.bulkUpdateTrainingsSessionAttendance,
      refetchQueries: [
        { query: AttendanceSessionSidepanelDocument, variables: { sessionId: String(eventId) } },
      ],
      awaitRefetchQueries: true,
    }
  )

  const [updateSessionAttendancesHours, { loading: updateHoursLoading }] = useMutation(
    UpdateSessionAttendancesDocument,
    {
      extractorFn: (data) => data.trainingsMutations.bulkUpdateTrainingsSessionAttendance,
      refetchQueries: [
        { query: AttendanceSessionSidepanelDocument, variables: { sessionId: String(eventId) } },
      ],
      awaitRefetchQueries: true,
    }
  )

  const { getSessionAttendanceTranslation, getDataCollectionSessionAttendanceColor } =
    useTrainingsDatatable()

  const source = useGraphqlDataCollectionSource(
    {
      document: AttendanceSessionSidepanelDocument,
      fetchPolicy: 'cache-and-network',
      mapData: (data) => data.trainings.sessionAccessMembershipsConnection.nodes,
      search: {
        enabled: true,
        sync: true,
      },
      mapVariables: ({ filters, sortings, search }) => {
        const teamIds = filters.team?.map((id) => id)
        const status = filters.status?.map((id) => id)
        const statusFilter = status?.map((item) => {
          if (item === 'pending') return 'not started'
          else if (item === 'inprogress') return 'started'
          else if (item === 'missing') return 'absent'
          else return 'completed'
        })
        track(trackingEvents.training.trainingSession.applyFilterAttendanceStatus, {
          selected_statusfilter: statusFilter,
        })
        return {
          teamIds: teamIds?.length ? teamIds : undefined,
          status: status?.length ? status : undefined,
          sort: sortings?.[0],
          search: search,
          sessionId: String(eventId),
        }
      },
      paginationType: 'pages',
      mapPagination: (data) => {
        return {
          total: data.trainings.sessionAccessMembershipsConnection.totalCount ?? 0,
        }
      },
      sortings: {
        firstName: {
          label: i18n.t('trainings.trainings_revamp.event_sidepanel.sortings.name'),
        },
        status: {
          label: i18n.t('trainings.trainings_revamp.event_sidepanel.sortings.status'),
        },
      },
      defaultSortings: {
        field: 'firstName',
        order: 'asc',
      },
      filters: {
        team: {
          type: 'in',
          label: i18n.t('trainings.trainings_revamp.event_sidepanel.filters.team'),
          options: {
            options: async () => {
              const { data } = await client.query({
                query: AttendanceSidepanelFilterTeamNamesDocument,
                fetchPolicy: 'cache-first',
              })
              return data.teams.teamsConnection.edges.map((edge) => ({
                label: edge.node.name,
                value: edge.node.id,
              }))
            },
          },
        },
        status: {
          type: 'in',
          label: i18n.t('trainings.trainings_revamp.event_sidepanel.filters.status'),
          options: {
            options: [
              {
                label: i18n.t('trainings.trainings_revamp.event_sidepanel.status.not_started'),
                value: 'pending',
              },
              {
                label: i18n.t('trainings.trainings_revamp.event_sidepanel.status.started'),
                value: 'inprogress',
              },
              {
                label: i18n.t('trainings.trainings_revamp.event_sidepanel.status.absent'),
                value: 'missing',
              },
              {
                label: i18n.t('trainings.trainings_revamp.event_sidepanel.status.completed'),
                value: 'completed',
              },
            ],
          },
        },
      },
      selectable: (item) => item.sessionAttendance?.id,
      bulkActions: (selectedItems) => {
        track(trackingEvents.training.trainingSession.selectParticipant, {
          selected_type: selectedItems?.allSelected ? 'all' : 'individual',
        })
        const checkedItems = selectedItems?.itemsStatus?.filter((s) => s.checked) ?? []
        const allCompleted =
          checkedItems.length > 0 &&
          checkedItems.every((s) => s.item.sessionAttendance?.status === 'completed')
        const modifyHoursDisabled = !allCompleted

        return {
          primary: [
            {
              id: 'update-attendance-completed',
              label: i18n.t('trainings.trainings_revamp.event_sidepanel.bulk_actions.completed'),
            },
            {
              id: 'update-attendance-in-progress',
              label: i18n.t('trainings.trainings_revamp.event_sidepanel.bulk_actions.started'),
            },
            {
              id: 'update-attendance-pending',
              label: i18n.t('trainings.trainings_revamp.event_sidepanel.bulk_actions.not_started'),
            },
            {
              id: 'update-attendance-missing',
              label: i18n.t('trainings.trainings_revamp.event_sidepanel.bulk_actions.absent'),
            },
          ],
          secondary: [
            {
              id: 'modify-hours',
              label: i18n.t('trainings.trainings_revamp.event_sidepanel.bulk_actions.modify_hours'),
              disabled: modifyHoursDisabled,
            },
          ],
        }
      },
    },
    [eventId]
  )

  const handleBulkAction = useCallback(
    async (
      actionId: string,
      selection: {
        allSelected: boolean | 'indeterminate'
        itemsStatus: ReadonlyArray<{
          item: AttendanceSessionEventSidepanelFragment
          checked: boolean
        }>
        selectedCount: number
      }
    ) => {
      if (actionId === 'modify-hours') {
        const checkedItems = selection.itemsStatus.filter((s) => s.checked)
        const firstCompletedDuration =
          checkedItems[0]?.item.sessionAttendance?.completedDuration ?? null
        const isSelectAll =
          selection.allSelected === true || selection.allSelected === 'indeterminate'

        if (isSelectAll) {
          const mappedVars = source.mappedVariables
          const excludedIds =
            selection.allSelected === 'indeterminate'
              ? selection.itemsStatus
                  .filter((item) => !item.checked)
                  .map((item) => item.item.sessionAttendance?.id)
                  .filter((id): id is string => id !== undefined)
              : []

          setModifyHoursParams({
            ids: excludedIds,
            all: true,
            sessionId: eventId,
            search: mappedVars?.search ?? undefined,
            teamIds: mappedVars?.teamIds ?? undefined,
            statusFilter: mappedVars?.status ?? undefined,
          })
        } else {
          const selectedIds = checkedItems
            .map((item) => item.item.sessionAttendance?.id)
            .filter((id): id is string => id !== undefined)

          if (selectedIds.length === 0) return

          setModifyHoursParams({ ids: selectedIds })
        }
        setModifyHoursModal({
          open: true,
          firstCompletedDuration:
            firstCompletedDuration != null ? Number(firstCompletedDuration) : null,
          selectedCount: selection.selectedCount,
        })
        return
      }

      const statusMap: Record<string, TrainingsSessionAttendanceStatusEnum> = {
        'update-attendance-completed': 'completed',
        'update-attendance-in-progress': 'inprogress',
        'update-attendance-pending': 'pending',
        'update-attendance-missing': 'missing',
      }
      const status = statusMap[actionId]
      if (!status) return

      let params: BulkUpdateParams
      const isSelectAll =
        selection.allSelected === true || selection.allSelected === 'indeterminate'

      if (isSelectAll) {
        const mappedVars = source.mappedVariables
        const teamIds = mappedVars?.teamIds ?? undefined
        const statusFilter = mappedVars?.status ?? undefined
        const search = mappedVars?.search ?? undefined

        const excludedIds =
          selection.allSelected === 'indeterminate'
            ? selection.itemsStatus
                .filter((item) => !item.checked)
                .map((item) => item.item.sessionAttendance?.id)
                .filter((id): id is string => id !== undefined)
            : []

        params = {
          status,
          all: true,
          sessionId: String(eventId),
          search,
          teamIds,
          statusFilter,
          ids: excludedIds,
        }
      } else {
        const selectedIds = selection.itemsStatus
          .filter((item) => item.checked)
          .map((item) => item.item.sessionAttendance?.id)
          .filter((id): id is string => id !== undefined)

        if (selectedIds.length === 0) return

        params = {
          status,
          ids: selectedIds,
        }
      }

      await bulkUpdateTrainingsSessionAttendance({
        variables: params,
      })
    },
    [eventId, source.mappedVariables, bulkUpdateTrainingsSessionAttendance]
  )

  const {
    access: { id: accessId },
  } = useCurrentSession()
  const { accessCalendarSync } = useAccessCalendarSync(accessId)

  const { reminders, loading: remindersLoading } = useLoadReminders(eventId ?? '')

  const { getModalityTranslation } = useTrainingsDatatable()
  const { data, loading } = useQuery(EventSidepanelNavigationDocument, {
    variables: { trainingClassIds: [String(classId)] },
    fetchPolicy: 'cache-first',
  })
  const { data: attendanceData } = useQuery(AttendanceSessionSidepanelDocument, {
    variables: { sessionId: String(eventId), perPage: 1 },
  })
  const allSessions = data?.trainings.sessionsConnection.nodes ?? []
  const currentEvent = allSessions.find((session) => session.id === String(eventId))
  const participantCount =
    attendanceData?.trainings.sessionAccessMembershipsConnection.totalCount ?? 0

  const hasCalendarInviteSent = (() => {
    if (currentEvent?.calendarInvite?.sent) return true
    if (currentEvent?.recurrent && !currentEvent?.calendarInvite) {
      const parentSession = allSessions.find(
        (session) =>
          session.recurrent && !!session.calendarInvite?.sent && session.id !== currentEvent.id
      )
      return !!parentSession
    }
    return false
  })()

  const formatDuration = (duration: string) => {
    const totalMinutes = parseFloat(duration) * 60
    const hours = Math.floor(totalMinutes / 60)
    const minutes = Math.round(totalMinutes % 60)
    return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}min`
  }

  const getDate = (date?: string | null, asUtc = false) => {
    if (!date) return null

    const parsedDate = asUtc ? moment.utc(date) : moment(date)
    return i18nDateToString(parsedDate, 'ui_dmy_long')
  }

  const getTime = (dateString: string) => {
    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }
    const time = new Date(dateString).toLocaleTimeString(undefined, timeOptions)
    return time
  }

  const getFrequencyTranslation = (frequency?: TrainingsSessionFrequencyEnum | null) => {
    if (!frequency) return null
    const frequencyMap: Record<TrainingsSessionFrequencyEnum, string> = {
      daily: i18n.t('trainings.trainings_revamp.event_sidepanel.header_details.frequency.daily'),
      weekly: i18n.t('trainings.trainings_revamp.event_sidepanel.header_details.frequency.weekly'),
      monthly: i18n.t(
        'trainings.trainings_revamp.event_sidepanel.header_details.frequency.monthly'
      ),
    }
    return frequencyMap[frequency]
  }

  const duration = currentEvent?.duration ? formatDuration(currentEvent?.duration) : '-'
  const sessionDurationDecimal = currentEvent?.duration ? parseFloat(currentEvent.duration) : 0

  const handleModifyHoursSave = async (completedDurationDecimal: number) => {
    if (!modifyHoursParams || !eventId) return
    const response = await updateSessionAttendancesHours({
      variables: {
        ...modifyHoursParams,
        completedDuration: String(completedDurationDecimal),
      },
    })
    const handled = handleResult(response, () => {
      app.setFlash({
        type: 'wrong',
        message:
          i18n.t('trainings.trainings_revamp.event_sidepanel.update_attendance_error_message') ??
          'Something went wrong',
      })
    })
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message:
          i18n.t('trainings.trainings_revamp.event_sidepanel.update_attendance_success_message') ??
          'Attendance updated successfully',
      })
      setModifyHoursModal((prev) => ({ ...prev, open: false }))
      setModifyHoursParams(null)
    }
  }

  const instructors =
    currentEvent?.sessionInstructors && currentEvent?.sessionInstructors?.length
      ? currentEvent?.sessionInstructors.map((instructor) => ({
          firstName: instructor.employee.firstName,
          lastName: instructor.employee.lastName,
          type: 'person' as const,
          src: buildUrl(instructor.employee?.avatar?.url ?? ''),
        }))
      : []

  const renderDetailsTab = () => (
    <Box gap={'s24'} flexDirection="column" padding="s16">
      <Box width="full" flexDirection="column" gap="s16">
        <Box width="full" flexDirection="row" gap="s12" justifyContent="spaceBetween">
          <Box flexDirection="column" gap="s6" width="s240">
            <Text size="200">
              {i18n.t('trainings.trainings_revamp.event_sidepanel.header_details.type_label')}
            </Text>
            <Box width="fit-content">
              {currentEvent?.schedule === 'selfpaced' ? (
                <F0TagDot
                  color="malibu"
                  text={i18n.t(
                    'trainings.trainings_revamp.event_sidepanel.header_details.self-paced'
                  )}
                />
              ) : (
                <F0TagDot
                  color="barbie"
                  text={i18n.t(
                    'trainings.trainings_revamp.event_sidepanel.header_details.scheduled'
                  )}
                />
              )}
            </Box>
          </Box>
          <Box flexDirection="column" gap="s6" width="s240">
            <Text size="200">
              {i18n.t('trainings.trainings_revamp.event_sidepanel.header_details.modality_label')}
            </Text>
            <Box width="fit-content">
              <F0TagRaw
                icon={
                  currentEvent?.modality === 'inperson'
                    ? Person
                    : currentEvent?.modality === 'online'
                      ? Desktop
                      : Suitcase
                }
                text={getModalityTranslation(currentEvent?.modality) || '-'}
              />
            </Box>
          </Box>
        </Box>
        <Box width="full" flexDirection="row" gap="s12" justifyContent="spaceBetween">
          <Box flexDirection="column" gap="s6" width="s240">
            <Text size="200">
              {i18n.t('trainings.trainings_revamp.event_sidepanel.header_details.date_label')}
            </Text>
            {currentEvent?.schedule == 'selfpaced' ? (
              <Text size="200" weight="medium" color="grey1000">
                {getDate(currentEvent?.startsAt) ?? ''} - {getDate(currentEvent?.dueDate) ?? ''}
              </Text>
            ) : (
              <Text size="200" weight="medium" color="grey1000">
                {getDate(currentEvent?.startsAt) ?? '-'}
              </Text>
            )}
          </Box>
          <Box flexDirection="column" gap="s6" width="s240">
            <Text size="200">
              {i18n.t('trainings.trainings_revamp.event_sidepanel.header_details.hour_label')}
            </Text>
            <Text size="200" weight="medium" color="grey1000">
              {currentEvent?.schedule == 'selfpaced'
                ? duration
                : i18n.t(
                    'trainings.trainings_revamp.event_sidepanel.header_details.session_time_frame',
                    {
                      starts: getTime(currentEvent?.startsAt),
                      ends: getTime(currentEvent?.endsAt),
                      duration: duration,
                    }
                  )}
            </Text>
          </Box>
        </Box>
        {currentEvent?.recurrent && (
          <Box width="full" flexDirection="row" gap="s12" justifyContent="spaceBetween">
            <Box flexDirection="column" gap="s6" width="s240">
              <Text size="200">
                {i18n.t(
                  'trainings.trainings_revamp.event_sidepanel.header_details.frequency_label'
                )}
              </Text>
              <Text size="200" weight="medium" color="grey1000">
                {getFrequencyTranslation(currentEvent?.frequency) ?? '-'}
              </Text>
            </Box>
            <Box flexDirection="column" gap="s6" width="s240">
              <Text size="200">
                {i18n.t('trainings.trainings_revamp.event_sidepanel.header_details.end_date_label')}
              </Text>
              <Text size="200" weight="medium" color="grey1000">
                {getDate(currentEvent?.dueDate) ?? '-'}
              </Text>
            </Box>
          </Box>
        )}
        <Box width="full" flexDirection="row" gap="s12" justifyContent="spaceBetween">
          <Box flexDirection="column" gap="s6" width="s240">
            <Text size="200">
              {i18n.t('trainings.trainings_revamp.event_sidepanel.header_details.location_label')}
            </Text>
            <Text size="200" weight="medium" color="grey1000">
              {currentEvent?.location ? currentEvent?.location : '-'}
            </Text>
          </Box>
          <Box flexDirection="column" gap="s6" width="s240">
            {currentEvent?.schedule == 'scheduled' && instructors.length > 0 ? (
              <>
                <Text size="200">
                  {i18n.t(
                    'trainings.trainings_revamp.event_sidepanel.header_details.instructors_label'
                  )}
                </Text>
                <F0AvatarList avatars={instructors} size="sm" type="person" max={3} />
              </>
            ) : (
              <>
                <Text size="200">
                  {i18n.t('trainings.trainings_revamp.event_sidepanel.header_details.link_label')}
                </Text>
                {currentEvent?.link ? (
                  <Link.Href href={currentEvent?.link} external title="Link">
                    <Text size="200" color="textLink">
                      {'Link'}
                    </Text>
                  </Link.Href>
                ) : (
                  '-'
                )}
              </>
            )}
          </Box>
        </Box>
        {currentEvent?.schedule == 'scheduled' && instructors.length > 0 && (
          <Box width="full" flexDirection="row" gap="s12" justifyContent="spaceBetween">
            <Box flexDirection="column" gap="s6" width="s240">
              <Text size="200">
                {i18n.t('trainings.trainings_revamp.event_sidepanel.header_details.link_label')}
              </Text>
              {currentEvent?.link ? (
                <Link.Href href={currentEvent?.link} external title="Link">
                  <Text size="200" color="textLink">
                    {'Link'}
                  </Text>
                </Link.Href>
              ) : (
                '-'
              )}
            </Box>
          </Box>
        )}
      </Box>
      <Card>
        <Card.Content>
          <Box flexDirection="column" gap="s8">
            <Box flexDirection="row" gap="s8" alignItems="center">
              <Text size="200" weight="medium" color="grey1000">
                {i18n.t('trainings.trainings_revamp.event_sidepanel.calendar_invite.title')}
              </Text>
              {hasCalendarInviteSent && accessCalendarSync?.profileName && (
                <Text size="200" color="grey600">
                  {i18n.t('trainings.trainings_revamp.event_sidepanel.calendar_invite.description')}
                </Text>
              )}
            </Box>
            <Box flexDirection="row" gap="s8" alignItems="center">
              <F0Icon icon={Envelope} size="sm" />
              <Text size="200" color="grey1000">
                {hasCalendarInviteSent && accessCalendarSync?.profileName
                  ? accessCalendarSync.profileName
                  : i18n.t(
                      'trainings.trainings_revamp.event_sidepanel.calendar_invite.no_invites_sent'
                    )}
              </Text>
            </Box>
          </Box>
        </Card.Content>
      </Card>
      <Card>
        <Card.Content>
          <Box flexDirection="column" gap="s12">
            <Box flexDirection="row" gap="s8" alignItems="center">
              <Text size="200" weight="medium" color="grey1000">
                {i18n.t('trainings.trainings_revamp.event_sidepanel.reminders.title')}
              </Text>
            </Box>
            {remindersLoading ? null : reminders && reminders.length > 0 ? (
              reminders.map((reminder) => (
                <Box key={reminder.id} flexDirection="row" gap="s8" alignItems="center">
                  <F0Icon icon={Bell} size="sm" />
                  <Text size="200" color="grey1000">
                    {reminder.name}
                  </Text>
                </Box>
              ))
            ) : !loading ? (
              <Box flexDirection="row" gap="s8" alignItems="center">
                <F0Icon icon={Bell} size="sm" />
                <Text size="200" color="grey1000">
                  {i18n.t('trainings.trainings_revamp.event_sidepanel.reminders.no_reminders')}
                </Text>
              </Box>
            ) : null}
          </Box>
        </Card.Content>
      </Card>
    </Box>
  )

  const renderAttendanceTab = () => (
    <Box gap={'s16'} flexDirection="column" padding="s16">
      <Text size="200" weight="medium" color="grey1000">
        {i18n.tp('trainings.trainings_revamp.event_sidepanel.attendance.participants_count', {
          count: participantCount,
        })}
      </Text>
      <OneDataCollection
        id="trainings/attendance/v1"
        storage={DEFAULT_DATA_COLLECTION_STORAGE}
        source={source}
        onBulkAction={handleBulkAction}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('trainings.trainings_revamp.event_sidepanel.table_columns.name'),
                  sorting: 'firstName',
                  render: (item) => ({
                    type: 'person',
                    value: {
                      src: buildUrl(item.access?.avatar ?? ''),
                      firstName: item.firstName ?? '',
                      lastName: item.lastName ?? '',
                    },
                  }),
                },
                {
                  label: i18n.t(
                    'trainings.trainings_revamp.event_sidepanel.table_columns.attendance'
                  ),
                  render: (item) => ({
                    type: 'status',
                    value: {
                      label: getSessionAttendanceTranslation(item.sessionAttendance?.status),
                      status: getDataCollectionSessionAttendanceColor(
                        item.sessionAttendance?.status
                      ),
                    },
                  }),
                },
                {
                  label: i18n.t(
                    'trainings.trainings_revamp.event_sidepanel.table_columns.completed_hours'
                  ),
                  render: (item: AttendanceSessionEventSidepanelFragment) => ({
                    type: 'text' as const,
                    value:
                      item.sessionAttendance?.completedDuration != null
                        ? `${formatDuration(item.sessionAttendance.completedDuration)}/${
                            currentEvent?.duration ? formatDuration(currentEvent.duration) : '-'
                          }`
                        : '-',
                  }),
                },
              ],
            },
          },
        ]}
      />
      <ModifyHoursModal
        open={modifyHoursModal.open}
        onClose={() => {
          setModifyHoursModal((prev) => ({ ...prev, open: false }))
          setModifyHoursParams(null)
        }}
        sessionDurationDecimal={sessionDurationDecimal}
        initialCompletedDurationDecimal={
          modifyHoursModal.selectedCount === 1
            ? (modifyHoursModal.firstCompletedDuration ?? 0)
            : sessionDurationDecimal
        }
        onSave={handleModifyHoursSave}
        saving={updateHoursLoading}
      />
    </Box>
  )

  return (
    <>
      <Tabs
        activeTabId={activeTab}
        setActiveTabId={(id: TabId) => setActiveTab(id)}
        tabs={[
          {
            id: 'details',
            label: i18n.t('trainings.trainings_revamp.event_sidepanel.tabs.details'),
          },
          {
            id: 'attendance',
            label: i18n.t('trainings.trainings_revamp.event_sidepanel.tabs.attendance'),
          },
        ]}
      />
      {activeTab === 'details' && renderDetailsTab()}
      {activeTab === 'attendance' && renderAttendanceTab()}
    </>
  )
}

export default AttendanceSidepanel
